import {Component, effect, inject, OnDestroy, OnInit, ViewContainerRef} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {AppAuthService} from '@services/core/auth/app-auth.service';
import {ModalService} from '@services/helpers/modal/modal.service';
import {
  Dashboard,
  DashboardLayout,
} from '@models/app/dashboard-layouts/DashboardLayout';
import {TenantService} from '@services/core/tenant/tenant.service';
import {Subject} from 'rxjs/internal/Subject';
import {takeUntil} from 'rxjs/operators';
import {ThemeService} from '@services/core/theme/theme.service';

import {AlertComponent} from "@shared/components/alert/alert.component";
import {WebsocketService} from "@app/areas/real-time/services/websocket.service";
import {ChatAuthService} from "@app/areas/chat/services/chat-auth.service";
import {ChatApiService} from "@app/areas/chat/services/chat-api.service";
import {DeviceIdService} from "@app/areas/device/services/device-id.service";
import {PractitionerUserData} from "@models/data/auth/AuthPractitionerUserData";
import { EnquiriesService } from './areas/crm/enquiries/services/enquiries.service'
import {UserOnboardingService} from "@app/areas/account/onboarding/services/user-onboarding.service";
import {
  MyOnboardingProgressComponent
} from "@app/areas/account/onboarding/components/my-onboarding-progress/my-onboarding-progress.component";
import { ImpersonationStateService } from '@app/areas/auth/services/impersonation-state.service';
import { SessionBootstrapService } from '@app/shared/services/core/session-bootstrap/session-bootstrap.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    RouterOutlet,
    AlertComponent,
    MyOnboardingProgressComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly chatAuth = inject(ChatAuthService);
  private readonly router = inject(Router);
  private unsub = new Subject<void>();
  loading = true;

  userDashboard = {
    showClinician: false,
    showDataManagement: false,
  };

  onboarding = inject(UserOnboardingService);
  impersonation = inject(ImpersonationStateService);
  private readonly sessionBootstrap = inject(SessionBootstrapService);

  constructor(
    public modalService: ModalService,
    public appAuthService: AppAuthService,
    private vcr: ViewContainerRef,
    private tenantService: TenantService,
    private theme: ThemeService,
    private ws: WebsocketService,
    private chatApi: ChatApiService,
    private device: DeviceIdService,
    private enquiries: EnquiriesService,
  ) {
    // subscribe to the user notification topic
    effect(() => {
      const ready = this.sessionBootstrap.ready$();
      const u = this.appAuthService.user$()
      const loading = this.shouldShowGlobalLoader(this.appAuthService.loading);
      const path = (this.router.url ?? '').split('?')[0].split('#')[0];

      console.debug('[AppComponent] user effect', {
        userId: u?.identityProfile?.id ?? null,
        ready,
        loading,
        publicRoute: this.isPublicRoute,
        path,
        loggedIn: this.appAuthService.loggedIn,
      });

      if (u === null) {
        if (loading) {
          console.debug('[AppComponent] user effect waiting for auth/bootstrap', {
            path,
          });
          return;
        }
        this.handleUserNotLoggedIn();
        this.impersonation.state$.set({ active: false });
        return;
      }

      if (!ready) {
        return;
      }

      const userId = u?.identityProfile?.id ?? null;
      if (!this.onboarding.loading$()) {
        const requiresOnboarding = this.onboarding.requiresOnboarding$();
        const isAdmin = this.appAuthService.isAdmin;

        if (path === '/account/login' && this.appAuthService.loggedIn) {
          if (!isAdmin && requiresOnboarding) {
            this.navigateToOnboarding();
          } else {
            this.navigateToDashboard();
          }
        } else if (!isAdmin && requiresOnboarding && !this.isPublicRoute && !path.startsWith('/onboarding')) {
          this.navigateToOnboarding();
        } else if (path.startsWith('/onboarding') && (isAdmin || !requiresOnboarding)) {
          this.navigateToDashboard();
        }
      }

      if (this.initialisedUser === userId) {
        return;
      }

      console.debug('[AppComponent] user changed', { userId, path });
      this.handleUserChanged(u)
      this.impersonation.syncState();
    })

    effect(() => {
      const loading = this.shouldShowGlobalLoader(this.appAuthService.loading);
      console.debug('[AppComponent] loader effect', {
        loading,
        authLoading: this.appAuthService.loading,
        bootstrapping: this.sessionBootstrap.bootstrapping$(),
        publicRoute: this.isPublicRoute,
      });
      this.showHideLoader(loading);
      this.setupDefaultTenant(loading);
      setTimeout(() => {
        this.loading = loading;
      });
    });

    // subscribing to the theme change after the DOM has been loaded in
    this.theme.theme$
      .pipe(takeUntil(this.unsub))
      .subscribe(t => {
        switch (t) {
          case 'dark':
            document.documentElement.classList.add('dark');
            break;
          case 'light':
            document.documentElement.classList.remove('dark');
            break;
        }
      })

    // Loading subscription
    this.appAuthService.loading$
      .pipe(takeUntil(this.unsub))
      .subscribe((loading) => this.handleUserLoading(loading));
  }

  ngOnInit(): void {
    this.setModalContainerReference();
  }

  ngOnDestroy(): void {
    this.unsub.next();
    this.unsub.complete();
  }

  get isPublicRoute(): boolean {
    const path = (this.router.url ?? '').split('?')[0].split('#')[0];

    return (
      path.startsWith('/account') ||
      path.startsWith('/secure-document-upload') ||
      path.startsWith('/consultation-invite')
    );
  }

  get shouldShowOnboardingProgress(): boolean {
    const path = (this.router.url ?? '').split('?')[0].split('#')[0];

    return this.appAuthService.loggedIn$()
      && this.onboarding.requiresOnboarding$()
      && !this.appAuthService.isAdmin
      && !this.appAuthService.isPatientOnly
      && !path.startsWith('/onboarding');
  }

  private handleUserLoading(loading: boolean): void {
    const shouldLoad = this.shouldShowGlobalLoader(loading);
    console.debug('[AppComponent] handleUserLoading', {
      authLoading: loading,
      bootstrapping: this.sessionBootstrap.bootstrapping$(),
      shouldLoad,
      publicRoute: this.isPublicRoute,
    });
    this.showHideLoader(shouldLoad);
    this.setupDefaultTenant(shouldLoad);
    setTimeout(() => {
      this.loading = shouldLoad;
    });
  }

  private initialisedUser : string | null = null

  private handleUserChanged(u: PractitionerUserData): void {
    const deviceId = this.device.get();
    const userId = u?.identityProfile?.id ?? '';

    // do not re-initialise the same user
    if (this.initialisedUser === userId) {
      return;
    }

    this.initialisedUser = userId
    this.enquiries.refresh();

    // rotate the user key
    this.chatAuth.ensureDeviceKeyOnServer(this.chatApi, deviceId)
      .then()
      .catch()

    void this.onboarding.refresh();
  }

  private handleUserNotLoggedIn(): void {
    this.ws.close()
    this.initialisedUser = null;
  }

  private navigateToDashboard(): void {
    this.router.navigate([this.appAuthService.isPatientOnly ? '/cn' : '/cn/patient-list']).then();
  }

  private navigateToOnboarding(): void {
    this.router.navigate(['/onboarding']).then();
  }

  private setModalContainerReference(): void {
    this.modalService.setVcr = this.vcr;
  }

  private showHideLoader(loading): void {
    const element = document.getElementById('pageLoader');
    console.debug('[AppComponent] showHideLoader', {
      loading,
      hasElement: !!element,
    });
    if (loading) {
      element.style.display = 'block'
      // element.setAttribute('style', 'display:block !important');
    } else {
      element.style.display = 'none'
      // element.setAttribute('style', 'display:none !important');
    }
  }

  private setupDefaultTenant(isLoadingAuth: boolean): void {
    if (!isLoadingAuth && this.appAuthService.loggedIn) {
      this.tenantService.init();
    }
  }

  private shouldShowGlobalLoader(authLoading: boolean): boolean {
    if (this.sessionBootstrap.ready$()) {
      return false;
    }

    return authLoading || this.sessionBootstrap.bootstrapping$();
  }

  private setupSelectedDashboard(selectedDashboard: DashboardLayout): void {
    this.userDashboard = {
      showClinician: (selectedDashboard?.type ?? null) === Dashboard.Clinician,
      showDataManagement:
        (selectedDashboard?.type ?? null) === Dashboard.DataManagement,
    };
  }
}
